import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  UserPlus,
  KeyRound,
  Copy,
  Check,
  Trash2,
  Power,
  PowerOff,
  Users,
  Share2,
} from "lucide-react";
import { STORAGE } from "@/lib/mr";
import { BrandWordmark } from "@/components/Brand";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin panel — friend account management. Reached from Setup ("Friends")
 * but only when the unlock token is the master APP_PASSWORD. Everything is
 * branded Hidden Aether — a friend whose token isn't admin will be sent
 * back to Setup by the guard below.
 */
export default function Admin() {
  const navigate = useNavigate();
  const isAdmin = useMemo(() => localStorage.getItem(STORAGE.IS_ADMIN) === "1", []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  // Plaintext password shown ONCE after create/reset. Keyed by user id.
  const [revealed, setRevealed] = useState({}); // {[id]: password}
  const [copiedId, setCopiedId] = useState("");

  // Guard: non-admin tokens go back to Setup.
  useEffect(() => {
    if (!isAdmin) navigate("/", { replace: true });
  }, [isAdmin, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load friends");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/users`);
        if (cancelled) return;
        setUsers(Array.isArray(res.data) ? res.data : []);
        setError("");
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Failed to load friends");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const createFriend = async (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    if (!label || creating) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/users`, { label });
      const { id, password } = res.data;
      setRevealed((m) => ({ ...m, [id]: password }));
      setNewLabel("");
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create friend");
    } finally {
      setCreating(false);
    }
  };

  const rotatePassword = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/users/${id}/reset`);
      setRevealed((m) => ({ ...m, [id]: res.data.password }));
      toast.success("New password issued");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password");
    }
  };

  const toggleDisabled = async (id, currentlyDisabled) => {
    try {
      await axios.patch(`${API}/admin/users/${id}`, { disabled: !currentlyDisabled });
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const deleteFriend = async (id, label) => {
    if (!window.confirm(`Remove "${label}" permanently? They will be locked out immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      setRevealed((m) => { const n = { ...m }; delete n[id]; return n; });
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const copyPassword = async (id, password) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1600);
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  // Build the message we hand off to the system share sheet. Kept short,
  // first-name only, no app name in the visible text — the URL alone reveals
  // the destination. Friend reads it on WhatsApp/iMessage/Signal/etc.
  const buildShareMessage = (label, password) => {
    const firstName = (label || "").trim().split(/\s+/)[0] || "there";
    return (
      `Hey ${firstName} — here's your private key:\n\n` +
      `${password}\n\n` +
      `Open ${window.location.origin} and enter it on the unlock screen. ` +
      `Keep it to yourself.`
    );
  };

  const sharePassword = async (id, label, password) => {
    const text = buildShareMessage(label, password);
    // Prefer the native share sheet on mobile — picks WhatsApp, Messages, Signal, etc.
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch (e) {
        // User cancelled or share unavailable; fall through to fallbacks.
        if (e?.name === "AbortError") return;
      }
    }
    // Desktop fallback: copy the full message to the clipboard.
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Message copied — paste it into your messaging app");
    } catch {
      // Final fallback: open the user's mail client.
      window.location.href = `mailto:?subject=Your%20private%20key&body=${encodeURIComponent(text)}`;
    }
  };

  if (!isAdmin) return null;

  return (
    <div
      className="min-h-screen w-full mr-luxe mr-grain"
      style={{
        background:
          "radial-gradient(140% 100% at 50% 0%, #2a0810 0%, #150407 60%, #08010a 100%)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="admin-page"
    >
      <div className="mr-dust" aria-hidden="true" />
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/70 hover:text-white transition"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] tracking-[0.36em] uppercase">Back</span>
          </button>
          <BrandWordmark size="sm" />
          <div className="w-12" />
        </div>

        <div className="mt-10 flex flex-col items-center mr-rise">
          <Users className="w-5 h-5" style={{ color: "rgba(214,201,140,0.85)" }} />
          <h1
            className="mt-2 text-[22px] font-light tracking-[0.34em] uppercase text-white"
            data-testid="admin-title"
          >
            The Circle
          </h1>
          <div className="mt-2 mr-rule" style={{ width: "3rem" }} />
          <p className="mt-3 text-[11px] tracking-[0.28em] uppercase text-white/55 text-center max-w-sm">
            Invite friends. Each receives a private key.
          </p>
        </div>

        {/* Add friend */}
        <Card
          className="mt-10 p-5 mr-corners border-white/10"
          style={{ background: "rgba(255,255,255,0.02)" }}
        >
          <form onSubmit={createFriend} className="flex flex-col gap-3" data-testid="add-friend-form">
            <label
              className="text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "rgba(214,201,140,0.7)" }}
            >
              New friend
            </label>
            <div className="flex gap-2">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Mark from improv"
                className="flex-1 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 rounded-xl"
                maxLength={60}
                data-testid="new-friend-label"
              />
              <Button
                type="submit"
                disabled={creating || !newLabel.trim()}
                className="rounded-xl px-5 text-[#f0ead4] disabled:opacity-40 mr-cta mr-cta-breath"
                style={{
                  background:
                    "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontSize: "11px",
                }}
                data-testid="create-friend-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {creating ? "…" : "Invite"}
              </Button>
            </div>
            <p className="text-[10px] text-white/40 leading-relaxed">
              A unique password will be generated. Copy it and send it privately —
              they enter it on the unlock screen.
            </p>
          </form>
        </Card>

        {/* Friends list */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3 px-1">
            <span
              className="text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "rgba(214,201,140,0.7)" }}
            >
              The Circle
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40" data-testid="friend-count">
              {users.length} {users.length === 1 ? "member" : "members"}
            </span>
          </div>

          {loading && (
            <div className="text-center text-white/50 text-[12px] py-10" data-testid="admin-loading">
              Loading…
            </div>
          )}

          {!loading && error && (
            <div className="text-center text-red-300/80 text-[12px] py-10" data-testid="admin-error">
              {error}
            </div>
          )}

          {!loading && !error && users.length === 0 && (
            <div
              className="text-center py-10 px-6 border border-dashed border-white/10 rounded-xl text-white/50 text-[12px]"
              data-testid="admin-empty"
            >
              No one yet. Invite your first friend above.
            </div>
          )}

          <div className="flex flex-col gap-3" data-testid="friend-list">
            {users.map((u) => {
              const pw = revealed[u.id];
              const usage = `${u.daily_count}/${u.daily_limit}`;
              const lastSeen = u.last_seen
                ? new Date(u.last_seen).toLocaleString(undefined, {
                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                  })
                : "never";
              return (
                <Card
                  key={u.id}
                  className="p-4 border-white/10"
                  style={{ background: u.disabled ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.03)", opacity: u.disabled ? 0.6 : 1 }}
                  data-testid={`friend-row-${u.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-[15px] font-medium truncate" data-testid="friend-label">
                          {u.label}
                        </span>
                        {u.disabled && (
                          <span
                            className="text-[9px] uppercase tracking-[0.24em] px-1.5 py-0.5 rounded"
                            style={{ background: "rgba(255,80,80,0.12)", color: "rgba(255,160,160,0.85)" }}
                          >
                            Revoked
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-white/45">
                        <span data-testid="friend-usage">Today {usage}</span>
                        <span>·</span>
                        <span>Seen {lastSeen}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => rotatePassword(u.id)}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition"
                        title="New password"
                        data-testid="rotate-pw-btn"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleDisabled(u.id, u.disabled)}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition"
                        title={u.disabled ? "Re-enable" : "Revoke"}
                        data-testid="toggle-disabled-btn"
                      >
                        {u.disabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteFriend(u.id, u.label)}
                        className="p-2 rounded-lg text-red-300/70 hover:text-red-300 hover:bg-white/5 transition"
                        title="Delete"
                        data-testid="delete-friend-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {pw && (
                    <div
                      className="mt-3 p-3 rounded-lg border flex items-center justify-between gap-3"
                      style={{
                        background: "rgba(214,201,140,0.06)",
                        borderColor: "rgba(214,201,140,0.25)",
                      }}
                      data-testid="revealed-password"
                    >
                      <div className="min-w-0">
                        <div
                          className="text-[9px] uppercase tracking-[0.36em] mb-1"
                          style={{ color: "rgba(214,201,140,0.85)" }}
                        >
                          Password (shown once)
                        </div>
                        <code
                          className="text-[15px] tracking-wider truncate block"
                          style={{ color: "#f0ead4", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                          data-testid="revealed-password-value"
                        >
                          {pw}
                        </code>
                      </div>
                      <div className="shrink-0 flex gap-2">
                        <button
                          onClick={() => sharePassword(u.id, u.label, pw)}
                          className="px-3 py-2 rounded-lg text-[10px] uppercase tracking-[0.24em] text-[#f0ead4] hover:brightness-110 transition flex items-center gap-1.5 border"
                          style={{
                            background: "rgba(214,201,140,0.08)",
                            borderColor: "rgba(214,201,140,0.35)",
                          }}
                          data-testid="share-password-btn"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Send
                        </button>
                        <button
                          onClick={() => copyPassword(u.id, pw)}
                          className="px-3 py-2 rounded-lg text-[10px] uppercase tracking-[0.24em] text-[#f0ead4] hover:brightness-110 transition flex items-center gap-1.5"
                          style={{
                            background: "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
                          }}
                          data-testid="copy-password-btn"
                        >
                          {copiedId === u.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedId === u.id ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
